import React, { useEffect, useState, useCallback, Suspense } from 'react';
import 'antd/dist/antd.css';
import {
  getAllUser,
  sendLink,
  getRoles,
  changeActivateUser,
  getDownloadLink,
  getDownloadLinkForCollector,
} from 'store/requests/definitions';
import { Modal, notification } from 'antd';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import trTR from 'antd/es/locale/tr_TR';
import { CSVLink } from 'react-csv';
import { ConfigProvider } from 'antd';
import {
  ALL_USER_ROLES,
  debouncedDataLoadWaitMs,
  FILE_TEMPLATE_CODE,
  OPERATIONAL_ROLES,
  ORDER_TYPES,
} from 'helpers/constants';
import {
  ExclamationCircleOutlined,
  FilterOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  PlusOutlined,
  DownloadOutlined,
  SendOutlined,
  UploadOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { Tooltip, Row, Button, Table, Col, Form, Tag, Dropdown } from 'antd';
import { useHistory } from 'react-router-dom';
import { Loading } from '../../../../components';
import userHaveRolePermission from 'helpers/userHaveRolePermission';
import { PAGE_URLS } from 'helpers/endpoints';
import { useSelector } from 'react-redux';
import ActivePassiveUpdate from '../../../../components/ImportExcel/ActivePassiveUpdate';
import ImportCourierAndCollectorList from 'components/ImportExcel/ImportCourierAndCollectorList';

const Add = React.lazy(() => import('./Add'));
const Edit = React.lazy(() => import('./Edit'));
const UsersTableFilters = React.lazy(() => import('./UsersTableFilters'));
const UserAndCourierSendLink = React.lazy(
  () => import('../../Definitions/Users/UserAndCourierSendLink'),
);
let lastQuery = false;

const UsersView = ({ route }) => {
  const history = useHistory();
  const { isViewEnabled, isCrudEnabled } = userHaveRolePermission(route?.name);
  if (!isViewEnabled) {
    history.push(`${PAGE_URLS.AUTH}${PAGE_URLS.CONTROL}`);
  }
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const { confirm } = Modal;
  const [filters, setFilters] = useState({});
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSendLinkModalOpen, setIsSendLinkModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [allRoles, setAllRoles] = useState([]);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [activePassiveUpdateModalOpen, setActivePassiveUpdateModalOpen] =
    useState(false);
  const activeRegionId =
    useSelector((state) => +state.auth?.user?.activeRegion?.id) ?? null;
  const activeStoreId =
    useSelector((state) => +state.auth?.user?.activeStore?.id) ?? null;
  const [batchExcelUrl, setBatchExcelUrl] = useState(null);
  const [activePassiveExcelUrl, setActivePassiveExcelUrl] = useState(null);

  const [tableParams, setTableParams] = useState({
    pagination: {
      showSizeChanger: true,
      showQuickJumper: true,
      showTotal: (total) => `Toplam ${total} kayıt`,
      pageSizeOptions: [20, 50, 100],
      size: 'small',
      current: 1,
      pageSize: 20,
    },
  });

  useEffect(() => {
    fetchData();
    setIsFiltersOpen(false);
    return () => {
      lastQuery = null;
      setLoading(false);
    };
  }, [JSON.stringify(tableParams), JSON.stringify(filters)]);

  useEffect(() => {
    (async () => {
      const getAllRoles = await getRoles().then((res) => res);
      const userRolesSelectOptionsList = getAllRoles.data.map((item) => {
        return { value: item, title: t(item) };
      });
      setAllRoles(userRolesSelectOptionsList);
      setBatchExcelUrl(FILE_TEMPLATE_CODE.COLLECTOR_CREATE_OR_UPDATE);
      setActivePassiveExcelUrl(FILE_TEMPLATE_CODE.USER_ACTIVE_PASSIVE);
    })();
  }, []);

  const fetchData = () => {
    const queryString = getQueryString();
    loadData(queryString);
  };

  const sendNotificationLink = (itemId, notificationRole) => {
    if (!notificationRole) {
      notification.warn({
        message: t('warning'),
        description: t('userNotCollectorOrCourier'),
      });
      return;
    }
    let data = {
      storeCodes: null,
      userIds: [itemId],
      role: notificationRole,
    };
    sendLink(data).then((res) => {
      if (res.isSuccess) {
        notificationSendLink('success');
      } else {
        notification.error({ message: res.message });
      }
    });
  };

  const columns = [
    {
      title: t('username'),
      dataIndex: 'username',
      sorter: true,
      showSorterTooltip: false,
    },
    {
      title: t('fullName'),
      dataIndex: 'nameSurname',
      sorter: true,
      showSorterTooltip: false,
    },
    {
      title: t('phoneNumber'),
      dataIndex: 'phoneNumber',
      sorter: true,
      showSorterTooltip: false,
      render: (phoneNumber, data) =>
        data.isActive ? phoneNumber : '**********',
    },
    {
      title: t('roles'),
      dataIndex: 'allRoles',
      sorter: true,
      showSorterTooltip: false,
    },
    {
      title: t('isActive'),
      dataIndex: 'isActive',
      sorter: true,
      showSorterTooltip: false,
      render: (isActive) =>
        isActive ? (
          <Tag color={'green'}>{t('active')}</Tag>
        ) : (
          <Tag color={'red'}>{t('passive')}</Tag>
        ),
    },
    {
      title: t('activeStore'),
      dataIndex: 'activeStore',
      sorter: true,
      showSorterTooltip: false,
      render: (store) => store?.name,
    },
    {
      title: t('activeRegion'),
      dataIndex: 'activeRegion',
      sorter: true,
      showSorterTooltip: false,
      render: (region) => region?.name,
    },
    {
      width: 50,
      render: (dataItem) =>
        isCrudEnabled && (
          <Dropdown
            menu={{
              items: [
                {
                  label: t('sendLink'),
                  icon: <SendOutlined />,
                  onClick: () => {
                    sendNotificationLink(
                      dataItem.id,
                      dataItem.notificationRole,
                    );
                  },
                },
                {
                  label: t('edit'),
                  icon: <EditOutlined />,
                  onClick: () => {
                    setIsEditModalOpen(true);
                    setSelectedUser(dataItem);
                  },
                },
                {
                  danger: dataItem.isActive ? true : false,
                  label: dataItem.isActive ? t('delete') : t('activate'),
                  icon: dataItem.isActive ? (
                    <DeleteOutlined />
                  ) : (
                    <PlusOutlined />
                  ),
                  onClick: () => {
                    dataItem.isActive
                      ? showDeleteConfirm(dataItem?.id)
                      : showActivateConfirm(dataItem?.id);
                  },
                },
              ],
            }}
            placement="bottomRight"
            arrow
            trigger={['click']}
          >
            <Button shape="circle" icon={<MoreOutlined />}></Button>
          </Dropdown>
        ),
    },
  ];

  const actions = [
    {
      isCrudEnabled,
      type: 'primary',
      title: t('add'),
      icon: <PlusOutlined />,
      onClick: () => setIsAddModalOpen(true),
      disabled: false,
      hasDropdown: false,
    },
    {
      isCrudEnabled: true,
      type: '',
      title: t('download'),
      customContent: (
        <CSVLink
          filename={'Users'}
          data={[
            columns.map((item) => item.title),
            ...rows.map((item) => [
              item.username,
              item.nameSurname,
              item.allRoles,
            ]),
          ]}
        >
          <Button
            style={{ marginRight: '3px' }}
            shape="round"
            icon={<DownloadOutlined />}
          ></Button>
        </CSVLink>
      ),
      onClick: () => {},
      disabled: false,
      hasDropdown: false,
    },
    {
      isCrudEnabled,
      type: '',
      title: t('sendLink'),
      icon: <SendOutlined />,
      onClick: () => setIsSendLinkModalOpen(true),
      disabled: false,
      hasDropdown: false,
    },
    {
      isCrudEnabled,
      type: '',
      title: t('import'),
      icon: <UploadOutlined />,
      onClick: () => setIsImportModalOpen(true),
      disabled: false,
      hasDropdown: true,
    },
  ];

  const renderActionButtons = () => {
    return actions.map((item, idx) => {
      if (item.hasDropdown) {
        return (
          item.isCrudEnabled &&
          (item.customContent ?? (
            <Dropdown
              key={idx}
              menu={{
                items: [
                  {
                    label: t('bulkAddAndUpdate'),
                    icon: <UploadOutlined />,
                    onClick: () => {
                      setIsImportModalOpen(true);
                    },
                  },
                  {
                    label: t('makeActiveOrPassive'),
                    icon: <UnorderedListOutlined />,
                    onClick: () => {
                      setActivePassiveUpdateModalOpen(true);
                    },
                  },
                ],
              }}
              placement="bottomRight"
              arrow
              trigger={['click']}
            >
              <Tooltip title={item.title} key={idx}>
                <Button
                  type={item.type}
                  shape="round"
                  icon={item.icon}
                  style={{ marginBottom: '10px', marginRight: '3px' }}
                  disabled={item.disabled}
                  danger={item.danger}
                />
              </Tooltip>
            </Dropdown>
          ))
        );
      } else {
        return (
          <Tooltip title={item.title} key={idx}>
            {item.isCrudEnabled &&
              (item.customContent ?? (
                <Button
                  type={item?.type}
                  shape="round"
                  icon={item?.icon}
                  onClick={item.onClick}
                  disabled={item.disabled}
                  style={{ marginBottom: '10px', marginRight: '3px' }}
                  danger={item?.danger}
                />
              ))}
          </Tooltip>
        );
      }
    });
  };

  function showDeleteConfirm(itemId) {
    confirm({
      title: t('modalDeleteTitle'),
      icon: <ExclamationCircleOutlined />,
      content: t('userDelete'),
      okText: t('delete'),
      okType: 'danger',
      cancelText: t('cancel'),
      centered: true,
      onOk() {
        changeActivateUser({ id: itemId, isActive: false }).then((res) => {
          if (res.isSuccess) {
            notificationDelete('success');
            lastQuery = null;
            const queryString = getQueryString();
            loadData(queryString);
          } else {
            notification.error({ message: res.message });
          }
        });
      },
      onCancel() {},
    });
  }

  function showActivateConfirm(itemId) {
    confirm({
      title: t('modalDeleteTitle'),
      content: t('userActivate'),
      okText: t('activate'),
      okType: 'success',
      cancelText: t('cancel'),
      centered: true,
      onOk() {
        changeActivateUser({ id: itemId, isActive: true }).then((res) => {
          if (res.isSuccess) {
            notificationAdd('success');
            lastQuery = null;
            const queryString = getQueryString();
            loadData(queryString);
          } else {
            notification.error({ message: res.message });
          }
        });
      },
      onCancel() {},
    });
  }

  const renderSelectedFilters = () => {
    return Object.keys(filters).map((item, idx) => {
      const value = filters[item];
      const isActiveField = item === 'active';
      const hasValue = isActiveField ? value !== undefined && value !== null : !!value;
      if (hasValue) {
        return (
          <Tag
            key={idx}
            closable
            color="geekblue"
            onClose={(e) => {
              e.preventDefault();
              removeItemFromFilters(item);
            }}
          >
            {`${t(item)} : ${t(`${value}`)}`}
          </Tag>
        );
      }
    });
  };

  const notificationAdd = (type) => {
    notification[type]({
      message: t('processCompleted'),
      description: t('addedUser'),
    });
  };

  const notificationDelete = (type) => {
    notification[type]({
      message: t('processCompleted'),
      description: t('userDeleted'),
    });
  };

  const notificationSendLink = (type) => {
    notification[type]({
      message: t('processCompleted'),
      description: t('sentLink'),
    });
  };

  const removeItemFromFilters = (destinationItem) => {
    Object.keys(filters).forEach((item) => {
      if (item === destinationItem) {
        const editedFilters = {
          ...filters,
          [destinationItem]: undefined,
        };
        setFilters(editedFilters);
        form.setFieldsValue(editedFilters);
      }
    });
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      ...sorter,
    });
  };

  const getQueryString = () => {
    let defaultUserNameSort = true;
    let queryString = 'filter=roles.role=out=COURIER and roles.active==true ';
    if (!filters.searchAllUsers) {
      if (activeStoreId) {
        queryString += `and roles.store.id==${activeStoreId}`;
      } else {
        queryString += `and ( roles.region.id==${activeRegionId} or roles.store.region.id==${activeRegionId} )`;
      }
    }
    Object.keys(filters).forEach((item, idx) => {
      const value = filters[item];
      const isActiveField = item === 'active';
      const hasValue = isActiveField ? value !== undefined && value !== null : !!value;
      if (hasValue && item !== 'searchAllUsers') {
        if (isActiveField) {
          queryString += ` and active==${value}`;
        } else {
          queryString += ` and ${item === 'roles' ? 'roles.role' : item}=='${encodeURI(value)}'`;
        }
      }
    });
    queryString = `${queryString}&page=${tableParams.pagination.current - 1}&size=${tableParams.pagination.pageSize}`;
    if (tableParams.column) {
      queryString = `${queryString}&sort=${tableParams.column.dataIndex},${ORDER_TYPES[tableParams.order]}`;
      defaultUserNameSort = false;
    }

    if (defaultUserNameSort) {
      queryString = `${queryString}&sort=username,asc`;
    }
    return queryString;
  };

  async function loadDataWithDebounce(queryString) {
    if (!loading) {
      lastQuery = queryString;
      setLoading(true);
      getAllUser(queryString)
        .then((userRequest) => {
          const { content, page } = userRequest;

          content &&
            content.forEach((element) => {
              let allRoles = '';
              if (OPERATIONAL_ROLES.includes(element?.roles[0]?.role)) {
                element.allRoles = t(element?.roles[0]?.role);
              } else {
                element.allRoles = [...new Set(element.roles.map(x => t(x.role)))];
              }

              element.allRoles.includes(t(ALL_USER_ROLES.COURIER))
                ? (element.notificationRole = 'COURIER')
                : element.allRoles.includes(t(ALL_USER_ROLES.COLLECTOR))
                  ? (element.notificationRole = 'COLLECTOR')
                  : (element.notificationRole = null);
              allRoles = [];
            });

          setRows(content ? content : []);
          setTableParams({
            ...tableParams,
            pagination: {
              ...tableParams.pagination,
              total: page.totalElements,
            },
          });
        })
        .finally(() => setLoading(false));
    }
  }

  const loadData = useCallback(
    debounce((queryString) => {
      loadDataWithDebounce(queryString);
    }, debouncedDataLoadWaitMs),
  );

  return (
    <div style={{ position: 'relative' }}>
      <Row
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'start',
        }}
      >
        <Col>{renderActionButtons()}</Col>
        <Col>
          <Button
            shape="round"
            icon={<FilterOutlined />}
            onClick={() => setIsFiltersOpen(!isFiltersOpen)}
          >
            {t('filters')}
          </Button>
        </Col>
      </Row>
      <Row style={{ display: 'flex', justifyContent: 'flex-end' }}>
        {renderSelectedFilters()}
      </Row>
      <Row style={{ marginTop: '10px' }}>
        <ConfigProvider locale={trTR}>
          <Table
            style={{ width: '100%' }}
            size="small"
            columns={columns}
            dataSource={rows}
            pagination={tableParams.pagination}
            loading={loading}
            onChange={handleTableChange}
            rowKey={(record) => record.id}
            scroll={{
              y: '70vh',
            }}
          />
        </ConfigProvider>
      </Row>
      <Suspense fallback={<Loading />}>
        {isFiltersOpen && (
          <UsersTableFilters
            setIsFiltersOpen={setIsFiltersOpen}
            setFilters={setFilters}
            form={form}
            allRoles={allRoles}
          />
        )}
        {isAddModalOpen && (
          <Add
            onCloseHandler={() => setIsAddModalOpen(false)}
            onSuccessCallback={fetchData}
            allRoles={allRoles}
          />
        )}
        {isEditModalOpen && (
          <Edit
            user={selectedUser}
            onCloseHandler={() => setIsEditModalOpen(false)}
            onSuccessCallback={fetchData}
            allRoles={allRoles}
          />
        )}
        {isSendLinkModalOpen && (
          <UserAndCourierSendLink
            onCloseHandler={() => setIsSendLinkModalOpen(false)}
            onSuccessCallback={fetchData}
          />
        )}
        {isImportModalOpen && (
          <ImportCourierAndCollectorList
            onCloseHandler={() => setIsImportModalOpen(false)}
            onSuccessCallback={fetchData}
            batchExcelUrl={batchExcelUrl}
            isCourier={false}
          />
        )}
        {activePassiveUpdateModalOpen && (
          <ActivePassiveUpdate
            onCloseHandler={() => setActivePassiveUpdateModalOpen(false)}
            onSuccessCallback={fetchData}
            activePassiveExcelUrl={activePassiveExcelUrl}
            isCourier={false}
          />
        )}
      </Suspense>
    </div>
  );
};

export default UsersView;
